export interface AgendaRow {
  label?: string
  text: string
  bold?: boolean
  textcolor?: string
}

export interface AgendaColumn {
  headerLabel: string
  headerColor: string
  headerBg?: string
  rowsBg?: string
  subheaderChip?: {
    border?: string
    icon: 'clock' | 'coffee'
    label: string
    bgColor: string
  }
  rows: AgendaRow[]
  isBreak?: boolean
  textcolor: string
}

export interface WorkstreamAgendaModalData {
  title: string
  layout?: 'card'
  columns: AgendaColumn[]
  thoughtStarters?: { bgColor: string; intro: string; questionColumns: string[][] }
}

export interface AgendaTableRow {
  time: string
  timeColor?: string
  phase?: string
  activity: string
  activityBold?: string
  textColor?: string
}

export interface AgendaTableModalData {
  title: string
  hasPhaseColumn: boolean
  rows: AgendaTableRow[]
}

const standardTemplateRows = (): AgendaRow[] => [
  { label: "8'", text: 'Individual completion of template' },
  { label: "15'", text: 'Each participant shares' },
  { label: "15'", text: 'Build on each other' },
  { label: "8'", text: 'Heat map (converge) for final answers' },
]

const finalColumnRows = (): AgendaRow[] => [
  { label: "15'", text: 'Collective completion based on previous inputs' },
  { label: "15'", text: 'Discussion/review' },
]

const breakColumn = (color: string): AgendaColumn => ({
  headerLabel: 'Break',
  headerColor: '#FFFFFF',
  isBreak: true,
  subheaderChip: { icon: 'clock', label: "15'", bgColor: color },
  rows: [{ text: 'Break', bold: true }],
  textcolor: color,
})

const workstreamAgendaModals: Record<string, WorkstreamAgendaModalData> = {
  'async-people-agenda': {
    title: 'People Virtual Session Agenda (3hr)',
    columns: [
      {
        headerLabel: '5 Beliefs',
        headerColor: '#FFA89D',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#FFA89D', border: '1px solid #EF8888' },
        rows: standardTemplateRows(),
        textcolor: '#940000',
      },
      {
        headerLabel: 'Surfacing Tensions',
        headerColor: '#FFA89D',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#FFA89D', border: '1px solid #EF8888' },
        rows: standardTemplateRows(),
        textcolor: '#940000',
      },
      breakColumn('#FFA89D'),
      {
        headerLabel: 'How They Use The Category',
        headerColor: '#FFA89D',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#FFA89D', border: '1px solid #EF8888' },
        rows: standardTemplateRows(),
        textcolor: '#940000',
      },
      {
        headerLabel: 'Who Are We For',
        headerColor: '#FFA89D',
        subheaderChip: { icon: 'clock', label: "30'", bgColor: '#FFA89D', border: '1px solid #EF8888' },
        rows: finalColumnRows(),
        textcolor: '#940000',
      },
    ],
    thoughtStarters: {
      bgColor: '#FFE8E4',
      intro:
        "When discussing the team's answers to each template, use this as an opportunity to stretch their thinking:",
      questionColumns: [
        [
          'How do these beliefs manifest in other categories beyond ours? What parallels can we draw?',
          'Which of these beliefs feel timeless versus temporary? Why?',
          'How might these beliefs evolve or be challenged in the next 3-5 years?',
        ],
        [
          'Are we identifying genuine human tensions, or just category frustrations? How can we tell the difference?',
          'Which of these tensions feel specific to our target versus universal to all humans?',
          'What societal or cultural forces are amplifying these tensions today?',
        ],
        [
          'Beyond functional benefits, what emotional territories are we not fully exploring yet?',
          'What category rituals or behaviors have remained unchanged for years? Why?',
          "Where do people 'bend the rules' of how our category is meant to be used? What does this tell us?",
        ],
        [
          'Does our shortlist feel distinct to our brand, or could it apply to any brand in our category?',
          'Are we being bold enough in identifying tensions that we can credibly help resolve?',
          'Have we maintained the human truth at the core, or have we drifted into category conventions?',
        ],
      ],
    },
  },
  'async-product-agenda': {
    title: 'Product Virtual Session Agenda (3hr)',
    columns: [
      {
        headerLabel: 'Capturing Ahas',
        headerColor: '#3DA9AE',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#A7E7ED', border: '1px solid #72D2FB' },
        rows: standardTemplateRows(),
        textcolor: '#0698E4',
      },
      {
        headerLabel: 'Us vs Category',
        headerColor: '#3DA9AE',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#A7E7ED', border: '1px solid #72D2FB' },
        rows: standardTemplateRows(),
        textcolor: '#0698E4',
      },
      breakColumn('#A7E7ED'),
      {
        headerLabel: 'Shortlist',
        headerColor: '#3DA9AE',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#A7E7ED', border: '1px solid #72D2FB' },
        rows: standardTemplateRows(),
        textcolor: '#0698E4',
      },
      {
        headerLabel: 'Product Edge in a Tweet',
        headerColor: '#3DA9AE',
        subheaderChip: { icon: 'clock', label: "30'", bgColor: '#A7E7ED', border: '1px solid #72D2FB' },
        rows: finalColumnRows(),
        textcolor: '#0698E4',
      },
    ],
    thoughtStarters: {
      bgColor: '#E8F7F5',
      intro:
        "When discussing the team's answers to each template, use this as an opportunity to stretch their thinking:",
      questionColumns: [
        [
          "What surprises you about our product that isn't fully leveraged yet?",
          'Where do functional benefits and sensorial cues intersect in unexpected ways?',
          'Which facts are we taking for granted that could be seen differently?',
        ],
        [
          'Which category must-haves could we elevate or reimagine in a distinctive way?',
          'What category norms exist simply because "it\'s always been done this way"?',
          'What can we own vs. the category norms?',
        ],
        [
          'Are we being specific enough in our functional benefits to be truly differentiated?',
          'How do our sensorial cues support and amplify our functional benefits?',
          'How future-proof is this edge? Could it be easily copied?',
        ],
        [
          'Does our edge feel both credible and meaningful to consumers?',
          'What reasons to believe would be the hardest for competitors to replicate?',
          'Are we articulating the edge in a way that connects to real human needs?',
        ],
      ],
    },
  },
  'async-brand-agenda': {
    title: 'Brand Virtual Session Agenda (3hr)',
    columns: [
      {
        headerLabel: 'Capturing Ahas',
        headerColor: '#91DDA4',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#91DDA4', border: '1px solid #3AAD2C' },
        rows: standardTemplateRows(),
        textcolor: '#006B3F',
      },
      {
        headerLabel: 'Us vs Category',
        headerColor: '#91DDA4',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#91DDA4', border: '1px solid #3AAD2C' },
        rows: standardTemplateRows(),
        textcolor: '#006B3F',
      },
      breakColumn('#91DDA4'),
      {
        headerLabel: 'Shortlist',
        headerColor: '#91DDA4',
        subheaderChip: { icon: 'clock', label: "45'", bgColor: '#91DDA4', border: '1px solid #3AAD2C' },
        rows: standardTemplateRows(),
        textcolor: '#006B3F',
      },
      {
        headerLabel: 'Brand Edge in a Tweet',
        headerColor: '#91DDA4',
        subheaderChip: { icon: 'clock', label: "30'", bgColor: '#91DDA4', border: '1px solid #3AAD2C' },
        rows: finalColumnRows(),
        textcolor: '#006B3F',
      },
    ],
    thoughtStarters: {
      bgColor: '#EDFAF0',
      intro:
        "When discussing the team's answers to each template, use this as an opportunity to stretch their thinking:",
      questionColumns: [
        [
          'Which emotional benefits have competitors left unexplored in their storytelling?',
          'What personality traits feel authentic to our heritage but could be expressed in fresh ways?',
          'Which distinctive assets are we under-leveraging or could we evolve?',
        ],
        [
          'Where are we following category codes that we could break in a meaningful way?',
          "What territories are competitors claiming that don't actually resonate with consumers?",
          "Which values could we uniquely own based on our brand's history and actions?",
        ],
        [
          'Could these personality traits belong to any other brand, or are they uniquely us?',
          'How do our emotional benefits ladder up to deeper human needs?',
          'Are our values actively shaping our behavior, or just nice words on paper?',
        ],
        [
          "Does our edge create a clear 'No' list of things we would never do?",
          'How does our edge show up beyond communications?',
          'What cultural conversation can we credibly lead with this edge?',
        ],
      ],
    },
  },
  'async-sprint-agenda': {
    title: 'Sprint | Live Session Agenda',
    layout: 'card',
    columns: [
      {
        headerLabel: 'Day 1',
        headerColor: '#FFA89D',
        headerBg:
          'linear-gradient(93.31deg, rgba(254, 86, 14, 0.5) 14.434%, rgba(169, 153, 126, 0.5) 54.62%, rgba(107, 200, 205, 0.5) 101.93%, rgba(107, 202, 161, 0.5) 145.8%, rgba(107, 203, 131, 0.5) 175.08%), #FFFFFF',
        rowsBg:
          'linear-gradient(77deg, rgba(254, 86, 14, 0.10) 29.43%, rgba(169, 153, 126, 0.10) 57.99%, rgba(107, 200, 205, 0.10) 91.61%, rgba(107, 202, 161, 0.10) 122.79%, rgba(107, 203, 131, 0.10) 143.6%), #FFF',
        rows: [
          {
            label: '09:00 - 12:00',
            text: "Each workstream presents their stimuli and proposals (1 hour per workstream: 30' presentation + 30' heatmap)",
          },
          { text: 'Lunch', bold: true, label: '12:00 - 13:00' },
          {
            label: '13:00 - 16:00',
            text: '3 cross functional teams now all take a shot at completing the 3 next templates (Building Relevance, Vow to Consumers, Mini Manifesto)',
          },
          { label: '16:00 - 17:00', text: "Presentations (30') and discussion (30')" },
          { label: '17:00 - 18:00', text: 'Landing on a common BVA (complete framework template)' },
        ],
        textcolor: '#00504D',
      },
      {
        headerLabel: 'Day 2',
        headerColor: '#2F8E5A',
        headerBg:
          'linear-gradient(91deg, rgba(254, 86, 14, 0.50) -85.99%, rgba(169, 153, 126, 0.50) -46.05%, rgba(107, 200, 205, 0.50) 0.97%, rgba(107, 202, 161, 0.50) 44.58%, rgba(107, 203, 131, 0.50) 73.68%), #FFF',
        rowsBg:
          'linear-gradient(77deg, rgba(254, 86, 14, 0.10) -52.11%, rgba(169, 153, 126, 0.10) -23.56%, rgba(107, 200, 205, 0.10) 10.05%, rgba(107, 202, 161, 0.10) 41.22%, rgba(107, 203, 131, 0.10) 62.03%), #FFF',
        rows: [
          {
            label: '10:00 - 11:00',
            text: 'Morning check-in with sponsor and vested OUs for feedback (refer to RAPID for guidance)',
          },
          { label: '11:00 - 12:00', text: 'Iterate on feedback and finalize BVA' },
          { text: 'Lunch', bold: true, label: '12:00 - 13:00' },
          {
            label: '13:00 - 16:00',
            text: 'Assess implications on: innovation pathways guidance, claims needs, critical occasions/demand spaces, engagement (comms and assets) - 1 hour for each',
          },
          {
            label: '16:00 - 17:00',
            text: 'Next steps: if necessary make arrangements for creating briefs on innovation, PP, BBEI and experiences. If the sponsor approves, you can proceed for approval from the Global CMO.',
          },
        ],
        textcolor: '#00504D',
      },
    ],
  },
}

const agendaTableModals: Record<string, AgendaTableModalData> = {
  'e2e-day1-agenda': {
    title: 'People | Indicative Agenda',
    hasPhaseColumn: true,
    rows: [
      {
        time: '10:00 - 12:00',
        timeColor: '#FDE5D9',
        textColor: '#00504D',
        phase: 'Onboarding, Immerse & Inspire',
        activityBold: 'Audience onboarding through curated research findings.',
        activity: 'Immersion into audience (social listening, consumer connects, shop-alongs, market visits).',
      },
      {
        time: '13:00 - 16:00',
        timeColor: 'rgba(196, 241, 207, 0.30)',
        textColor: '#00504D',
        phase: 'Diverge & Explore',
        activity:
          'Discover the extraordinary; insights and most promising "Freaky Facts". Teamwork: work through People templates to surface Human Tension.',
      },
      {
        time: '16:00 - 17:00',
        timeColor: 'rgba(159, 227, 231, 0.43)',
        textColor: '#00504D',
        phase: 'Conclude & Close',
        activity: 'Converge and land on summary templates.',
      },
    ],
  },
  'e2e-day2-agenda': {
    title: 'Product | Indicative Agenda',
    hasPhaseColumn: true,
    rows: [
      {
        time: '10:00 - 12:00',
        timeColor: '#FDE5D9',
        textColor: '#00504D',
        phase: 'Onboarding, Immerse & Inspire',
        activity:
          'Category U&A / motivation triggers, Mind Maps ingredients of future trends / sensorial mapping Superiority study, Product Strength.',
      },
      {
        time: '13:00 - 16:00',
        timeColor: 'rgba(196, 241, 207, 0.30)',
        textColor: '#00504D',
        phase: 'Diverge & Explore',
        activity:
          'Competitive product landscape review. Teamwork: work through Product templates to explore potential Product Edges.',
      },
      {
        time: '14:00 - 17:00',
        timeColor: 'rgba(159, 227, 231, 0.43)',
        textColor: '#00504D',
        phase: 'Conclude & Close',
        activity: 'Converge and land on summary templates.',
      },
    ],
  },
  'e2e-day3-agenda': {
    title: 'Brand | Indicative Agenda',
    hasPhaseColumn: true,
    rows: [
      {
        time: '10:00 - 12:00',
        timeColor: '#FDE5D9',
        textColor: '#00504D',
        phase: 'Onboarding, Immerse & Inspire',
        activity:
          'Brand audit & creative strategy overview. Heritage & archives review. VIS & distinctive assets. Competitive brand landscape review. Brand Philosophy/Pricing structure discussion.',
      },
      {
        time: '13:00 - 16:00',
        timeColor: 'rgba(196, 241, 207, 0.30)',
        textColor: '#00504D',
        phase: 'Diverge & Explore',
        activity: 'Teamwork: work through Brand templates to explore potential Brand Edges.',
      },
      {
        time: '16:00 - 17:00',
        timeColor: 'rgba(159, 227, 231, 0.43)',
        textColor: '#00504D',
        phase: 'Conclude & Close',
        activity: 'Converge and land on summary templates.',
      },
    ],
  },
  'e2e-day4-agenda': {
    title: 'Sprint Workstream | Indicative Agenda',
    hasPhaseColumn: true,
    rows: [
      {
        time: '10:00 - 12:00',
        timeColor: '#FDE5D9',
        textColor: '#00504D',
        phase: 'Onboarding, Immerse & Inspire',
        activity:
          'Connecting the dots: land on Meaningful Brand Role. Mood boarding + manifesto exercise. Sponsor & HXC check-in.',
      },
      {
        time: '13:00 - 16:00',
        timeColor: 'rgba(196, 241, 207, 0.30)',
        textColor: '#00504D',
        phase: 'Diverge & Explore',
        activity: 'Capture input & determine needed refinements. Refinement work.',
      },
      {
        time: '16:00 - 17:00',
        timeColor: 'rgba(159, 227, 231, 0.43)',
        textColor: '#00504D',
        phase: 'Conclude & Close',
        activity: 'Converge and land on summary templates.',
      },
    ],
  },
  'e2e-day5-agenda': {
    title: 'Implication Assessment | Indicative Agenda',
    hasPhaseColumn: false,
    rows: [
      {
        time: '10:00 - 11:00',
        timeColor: '#FDE5D9',
        textColor: '#00504D',
        activityBold: 'Pressure test against priority demand spaces:',
        activity:
          'run through a simple exercise of reviewing your existing demand spaces. Based on the new BVA, do they remain relevant?',
      },
      {
        time: '11:00 - 12:00',
        timeColor: 'rgba(196, 241, 207, 0.30)',
        textColor: '#00504D',
        activity:
          'Pressure test for designing experiences: virtual connect with WPP to present the BVA and assess how fertile it is. If they received a brief today, what would they find intriguing, confusing, missing?',
      },
      {
        time: '13:00 - 14:00',
        timeColor: 'rgba(159, 227, 231, 0.43)',
        textColor: '#00504D',
        activity:
          'Pressure test for product innovation: virtual connect with R&D to assess if this BVA could easily spark new innovations that stand out in the market, amplify the brand promise, and be commercially viable.',
      },
      {
        time: '14:00 - 15:00',
        timeColor: 'rgba(159, 193, 231, 0.43)',
        textColor: '#00504D',
        activity:
          'Next steps: secure input, alignment and approval from OU stakeholders, sponsor, and - as a last step - the Global CMO.',
      },
    ],
  },
}

export const getWorkstreamAgendaModalData = (id: string): WorkstreamAgendaModalData | undefined =>
  workstreamAgendaModals[id]
export const getAgendaTableModalData = (id: string): AgendaTableModalData | undefined => agendaTableModals[id]
